import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { STUDENT_NAV } from "@/pages/student/StudentHome";
import { ADVISOR_NAV } from "@/pages/advisor/AdvisorHome";
import { EMPLOYER_NAV } from "@/pages/employer/EmployerHome";
import { ADMIN_NAV } from "@/pages/admin/AdminHome";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FinePrint } from "@/components/FinePrint";
import { Loader2, Lightbulb, Send } from "lucide-react";

const navFor = (role) => ({ student: STUDENT_NAV, advisor: ADVISOR_NAV, faculty: ADVISOR_NAV, employer: EMPLOYER_NAV, admin: ADMIN_NAV }[role] || STUDENT_NAV);
const STATUS_LABEL = { submitted: "Submitted", under_review: "Under Review", needs_info: "Needs Info", accepted: "Accepted", planned: "Planned", in_development: "In Development", implemented: "Implemented", declined: "Declined", duplicate: "Duplicate" };
const STATUS_CLS = { submitted: "bg-slate-100 text-slate-600", under_review: "bg-sky-100 text-sky-800", needs_info: "bg-amber-100 text-amber-800", accepted: "bg-teal-100 text-teal-800", planned: "bg-indigo-100 text-indigo-800", in_development: "bg-purple-100 text-purple-800", implemented: "bg-emerald-100 text-emerald-800", declined: "bg-red-100 text-red-700", duplicate: "bg-slate-100 text-slate-400" };

export default function SubmitSuggestion() {
  const { user } = useAuth();
  const location = useLocation();
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({ title: "", category: "Improvement", description: "", why: "" });
  const [mine, setMine] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/suggestions/mine").then(({ data }) => setMine(data)).catch(() => setMine([]));
  useEffect(() => { api.get("/suggestions/categories").then(({ data }) => setCats(data.categories || [])).catch(() => {}); load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/suggestions", { ...form, page: document.referrer || location.pathname });
      toast.success(data.message);
      setForm({ title: "", category: "Improvement", description: "", why: "" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not submit"); }
    finally { setBusy(false); }
  };

  return (
    <DashboardShell title="Submit a Suggestion" subtitle="Share ideas to improve GPA+ — we read every one" nav={navFor(user?.role)}>
      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-6 card-elevate">
          <div className="mb-4 flex items-center gap-2 text-[#003057]"><Lightbulb className="h-5 w-5 text-[#78be21]" /><h3 className="font-heading text-lg font-bold">Your idea</h3></div>
          <form onSubmit={submit} className="space-y-4" data-testid="suggestion-form">
            <div><Label>Suggestion title</Label><Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} required data-testid="sugg-title" className="mt-1.5" /></div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger className="mt-1.5" data-testid="sugg-category"><SelectValue /></SelectTrigger>
                <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Describe your suggestion</Label><Textarea rows={4} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} required data-testid="sugg-desc" className="mt-1.5" /></div>
            <div><Label>Why would this be helpful? <span className="text-slate-400">(optional)</span></Label><Textarea rows={2} value={form.why} onChange={(e) => setForm((f) => ({ ...f, why: e.target.value }))} data-testid="sugg-why" className="mt-1.5" /></div>
            <Button type="submit" disabled={busy} className="w-full rounded-full bg-[#003057] py-5 font-bold hover:bg-[#00223f]" data-testid="sugg-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-1.5 h-4 w-4" /> Submit suggestion</>}</Button>
          </form>
          <FinePrint>Suggestions are proposals reviewed by GPA+ administration — submitting one does not change GPA+ functionality or methodology.</FinePrint>
        </Card>

        <Card className="p-6 card-elevate">
          <h3 className="mb-4 font-heading text-lg font-bold text-[#003057]">My suggestions</h3>
          {!mine && <div className="flex h-32 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#003057]" /></div>}
          {mine && mine.length === 0 && <p className="text-sm text-slate-400" data-testid="sugg-empty">You haven't submitted any suggestions yet.</p>}
          {mine && mine.length > 0 && (
            <ul className="space-y-2">
              {mine.map((s) => (
                <li key={s.id} className="rounded-lg border border-slate-200 p-3" data-testid={`sugg-mine-${s.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate font-semibold text-[#003057]">{s.title}</span>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${STATUS_CLS[s.status]}`}>{STATUS_LABEL[s.status]}</span>
                  </div>
                  <p className="mt-0.5 text-xs text-slate-400">{s.category} · {s.created_at?.slice(0, 10)}</p>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </DashboardShell>
  );
}
